import { localDay, type ReportStatus } from '@quickreport/domain';
import type { LocalDb, Tx } from '@quickreport/sync';

export interface DashboardOptions {
  orgId: string;
  /** First and last day shown, as YYYY-MM-DD in the person's own time zone. */
  from: string;
  to: string;
  /** The person's time zone, so a report made at 11 pm counts on that day. */
  timeZone: string;
  /** Include money: billed, received, outstanding. Only for people who may see finance. */
  finance: boolean;
  /** Today in the person's calendar (YYYY-MM-DD), for the due counts. Defaults to this computer's today. */
  today?: string;
}

export interface DashboardDay {
  day: string;
  created: number;
  signed: number;
  /** Paise received that day. 0 unless finance is on. */
  receivedPaise: number;
  /** Paise spent that day. 0 unless finance is on. */
  spentPaise: number;
}

export interface DashboardData {
  from: string;
  to: string;
  days: DashboardDay[];
  kpi: {
    created: number;
    signed: number;
    /** Not yet signed, whatever day they were made. */
    waiting: number;
    /** Median hours from collection to first signature, or null when nothing was signed. */
    medianTurnaroundHours: number | null;
    /** Reports promised for today, not yet signed or marked complete. */
    dueToday: number;
    /** Promised for an earlier day and still open. */
    overdue: number;
  };
  /** Status of the reports made in the period. */
  statuses: { status: ReportStatus; n: number }[];
  topTests: { name: string; n: number }[];
  samples: { type: string; n: number }[];
  /** Null when finance is off. */
  finance: null | {
    billedPaise: number;
    receivedPaise: number;
    /** Still owed on every report, not only this period's. */
    outstandingPaise: number;
    /** Money spent in the period (expenses). */
    expensesPaise: number;
    expensesByCategory: { category: string; paise: number }[];
    byMethod: { method: string; paise: number; n: number }[];
    owing: {
      id: string;
      name: string;
      labNo: string;
      patient: string;
      duePaise: number;
      paidPaise: number;
      createdAt: string;
    }[];
  };
}

const MAX_DAYS = 366;

/** Start of `day` in the time zone, as a timestamp the database can compare with. */
const startOf = (p: string) => `(${p}::date)::timestamp at time zone $2`;
const DUE = 'greatest(r.price_paise - r.discount_paise, 0)';
const PAID = `coalesce((select sum(p.amount_paise) from payments p where p.report_id = r.id and p.deleted_at is null), 0)`;

/** Everything on the dashboard, from this device's own data, so it works offline. */
export async function loadDashboard(
  db: Pick<LocalDb, 'query'> | Tx,
  o: DashboardOptions,
): Promise<DashboardData> {
  const dayOk = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(d));
  if (!dayOk(o.from) || !dayOk(o.to)) throw new Error('Dates must look like 2026-10-31.');
  let from = o.from;
  const to = o.to < o.from ? o.from : o.to;
  const span = Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1;
  if (span > MAX_DAYS) {
    const d = new Date(Date.parse(to));
    d.setUTCDate(d.getUTCDate() - (MAX_DAYS - 1));
    from = d.toISOString().slice(0, 10);
  }
  // $1 org, $2 time zone, $3 first day, $4 last day. Reports are "in the period" when made then.
  const base = [o.orgId, o.timeZone, from, to];
  const inPeriod = (col: string) =>
    `${col} >= ${startOf('$3')} and ${col} < ${startOf('($4::date + 1)')}`;
  const q = async <T>(sql: string, params: unknown[] = base) =>
    (await db.query<T>(sql, params)).rows;

  const days = await q<{
    day: string;
    created: number;
    signed: number;
    received: number;
    spent: number;
  }>(`
    select to_char(d.day, 'YYYY-MM-DD') as day,
           coalesce(c.n, 0)::int as created,
           coalesce(s.n, 0)::int as signed,
           ${o.finance ? 'coalesce(p.s, 0)::int' : '0'} as received,
           ${o.finance ? 'coalesce(x.s, 0)::int' : '0'} as spent
      from generate_series($3::date, $4::date, interval '1 day') as d(day)
      left join (select (r.created_at at time zone $2)::date as day, count(*) as n
                   from reports r where r.org_id = $1 and r.deleted_at is null
                    and ${inPeriod('r.created_at')} group by 1) c on c.day = d.day
      left join (select (v.signed_at at time zone $2)::date as day, count(distinct v.report_id) as n
                   from report_versions v where v.org_id = $1 and v.version = 1
                    and ${inPeriod('v.signed_at')} group by 1) s on s.day = d.day
      left join (select (p.paid_at at time zone $2)::date as day, sum(p.amount_paise) as s
                   from payments p where p.org_id = $1 and p.deleted_at is null
                    and ${inPeriod('p.paid_at')} group by 1) p on p.day = d.day
      left join (select e.spent_on as day, sum(e.amount_paise) as s
                   from expenses e where e.org_id = $1 and e.deleted_at is null
                    and e.spent_on >= $3::date and e.spent_on <= $4::date group by 1) x on x.day = d.day
     order by d.day`);

  const waiting = (
    await db.query<{ n: number }>(
      `select count(*)::int as n from reports r where r.org_id = $1 and r.deleted_at is null
          and r.status in ('draft','awaiting_verification','awaiting_signature')`,
      [o.orgId],
    )
  ).rows;
  const due = (
    await db.query<{ today: number; overdue: number }>(
      `select count(*) filter (where due_date = $2::date)::int as today,
              count(*) filter (where due_date < $2::date)::int as overdue
         from reports where org_id = $1 and deleted_at is null and due_date is not null
          and due_date <= $2::date and due_done_at is null and status not in ('final', 'revoked')`,
      [o.orgId, o.today ?? localDay()],
    )
  ).rows[0];
  const tat = await q<{ h: number | null }>(`
    select (percentile_cont(0.5) within group (
             order by extract(epoch from (v.signed_at - r.collected_at)) / 3600))::float8 as h
      from report_versions v join reports r on r.id = v.report_id
     where v.org_id = $1 and v.version = 1 and r.deleted_at is null and r.collected_at is not null
       and v.signed_at >= r.collected_at and ${inPeriod('v.signed_at')}`);
  const statuses = await q<{ status: ReportStatus; n: number }>(`
    select r.status::text as status, count(*)::int as n from reports r
     where r.org_id = $1 and r.deleted_at is null and ${inPeriod('r.created_at')}
     group by 1 order by 2 desc`);
  const topTests = await q<{ name: string; n: number }>(`
    select s->'doc'->>'name' as name, count(*)::int as n
      from reports r, jsonb_array_elements(r.content->'sections') s
     where r.org_id = $1 and r.deleted_at is null and r.content->>'kind' <> 'generic'
       and ${inPeriod('r.created_at')} and s->'doc'->>'name' is not null
     group by 1 order by 2 desc, 1 limit 8`);
  const samples = await q<{ type: string; n: number }>(`
    select initcap(lower(trim(s->>'type'))) as type, count(distinct s->>'id')::int as n
      from reports r, jsonb_array_elements(r.content->'samples') s
     where r.org_id = $1 and r.deleted_at is null and ${inPeriod('r.created_at')}
       and trim(coalesce(s->>'type', '')) <> ''
     group by 1 order by 2 desc, 1 limit 8`);

  let finance: DashboardData['finance'] = null;
  if (o.finance) {
    const totals = await q<{ billed: number; received: number; outstanding: number }>(`
      select
        (select coalesce(sum(${DUE}), 0) from reports r
          where r.org_id = $1 and r.deleted_at is null and ${inPeriod('r.created_at')})::float8 as billed,
        (select coalesce(sum(p.amount_paise), 0) from payments p
          where p.org_id = $1 and p.deleted_at is null and ${inPeriod('p.paid_at')})::float8 as received,
        (select coalesce(sum(greatest(${DUE} - ${PAID}, 0)), 0) from reports r
          where r.org_id = $1 and r.deleted_at is null)::float8 as outstanding`);
    const byMethod = await q<{ method: string; paise: number; n: number }>(`
      select p.method, sum(p.amount_paise)::float8 as paise, count(*)::int as n from payments p
       where p.org_id = $1 and p.deleted_at is null and ${inPeriod('p.paid_at')}
       group by 1 order by 2 desc`);
    const spent = await q<{ category: string; paise: number }>(
      `select category, sum(amount_paise)::float8 as paise from expenses
        where org_id = $1 and deleted_at is null and spent_on >= $2::date and spent_on <= $3::date
        group by category order by 2 desc`,
      [o.orgId, from, to],
    );
    const owing = await q<{
      id: string;
      name: string;
      lab_no: string;
      patient: string;
      due: number;
      paid: number;
      created_at: string;
    }>(
      `
      select r.id, r.name, r.lab_no, coalesce(r.content->'patient'->>'name', '') as patient,
             ${DUE}::int as due, ${PAID}::int as paid, r.created_at
        from reports r
       where r.org_id = $1 and r.deleted_at is null and ${DUE} > ${PAID}
       order by (${DUE} - ${PAID}) desc, r.created_at limit 8`,
      [o.orgId],
    );
    const t = totals[0];
    finance = {
      billedPaise: Number(t?.billed ?? 0),
      receivedPaise: Number(t?.received ?? 0),
      outstandingPaise: Number(t?.outstanding ?? 0),
      expensesPaise: spent.reduce((n, x) => n + Number(x.paise), 0),
      expensesByCategory: spent.map((x) => ({ category: x.category, paise: Number(x.paise) })),
      byMethod: byMethod.map((m) => ({ method: m.method, paise: Number(m.paise), n: m.n })),
      owing: owing.map((x) => ({
        id: x.id,
        name: x.name,
        labNo: x.lab_no,
        patient: x.patient,
        duePaise: Number(x.due),
        paidPaise: Number(x.paid),
        createdAt: x.created_at,
      })),
    };
  }

  const dayRows: DashboardDay[] = days.map((d) => ({
    day: d.day,
    created: d.created,
    signed: d.signed,
    receivedPaise: Number(d.received),
    spentPaise: Number(d.spent),
  }));
  return {
    from,
    to,
    days: dayRows,
    kpi: {
      created: dayRows.reduce((n, d) => n + d.created, 0),
      signed: dayRows.reduce((n, d) => n + d.signed, 0),
      waiting: waiting[0]?.n ?? 0,
      medianTurnaroundHours: tat[0]?.h == null ? null : Number(tat[0].h),
      dueToday: due?.today ?? 0,
      overdue: due?.overdue ?? 0,
    },
    statuses,
    topTests,
    samples,
    finance,
  };
}
